import { DocumentType, gql } from "__generated__/gql";
import { useBrandMutation } from "utils/apollo/hooks/brand";
import DeleteConfirmModalButton from "components/Shared/ConfirmModal/DeleteConfirmModalButton";
import { removeDeletedRecords } from "utils/apollo/cacheHelpers";
import { assertPresence } from "utils/assertions";
import useAsyncCompleteToast from "components/Shared/useAsyncCompleteToast";

const deleteProductLineMutation = gql(/* GraphQL */ `
  mutation DeleteProductLine($input: DeleteProductLineInput!) {
    deleteProductLine(input: $input) {
      productLine {
        id
        name
      }
    }
  }
`);

export type DeleteProductLineData = DocumentType<
  typeof deleteProductLineMutation
>;

type Props = {
  onCompleted?: (data: DeleteProductLineData) => void;
  productLine: { id: UUID; name: string };
};

export default function DeleteProductLineButton({
  onCompleted,
  productLine,
}: Props) {
  const { showSuccessToast } = useAsyncCompleteToast();

  const deleteMutation = useBrandMutation(deleteProductLineMutation, {
    variables: { input: { id: productLine.id } },
    onCompleted: (data) => {
      onCompleted?.(data);
      const deletedProductLine = data?.deleteProductLine?.productLine;
      assertPresence(deletedProductLine);
      showSuccessToast(`Deleted ${deletedProductLine.name}`, {
        status: "warning",
      });
    },
    update: (cache, result) => {
      const deletedProductLine = result.data?.deleteProductLine?.productLine;
      assertPresence(deletedProductLine);
      removeDeletedRecords({
        cache,
        baseFieldName: "productLines",
        deletedRecords: [deletedProductLine],
      });
    },
  });

  return (
    <DeleteConfirmModalButton
      confirmText={
        <>
          Are you sure you want to delete the <b>{productLine.name}</b> product
          line and all of its SKUs? You won’t be able to undo this action
          afterwards.
        </>
      }
      label="Permanently Delete Product Line"
      mutation={deleteMutation}
      buttonProps={{ opacity: 0.6, size: "md" }}
      tooltipProps={{ placement: "bottom" }}
    />
  );
}
